/** @typedef {{ key: string, label: string, digitsMin: number, digitsMax: number, placeholder: string }} BranchField */
/** @typedef {{ code: string, name: string, bankNamePlaceholder: string, accountLabel: string, accountDigitsMin: number, accountDigitsMax: number, branch: BranchField | null, payoutNote: string, dialogSubtitle: string }} BankingProfile */

export const DEFAULT_BANK_COUNTRY = "GB";

/** @type {BankingProfile[]} */
export const BANKING_COUNTRIES = [
    {
        code: "GB",
        name: "United Kingdom",
        bankNamePlaceholder: "Barclays, Nationwide, HSBC, Lloyds…",
        accountLabel: "Account number",
        accountDigitsMin: 8,
        accountDigitsMax: 8,
        branch: {
            key: "sort_code",
            label: "Sort code",
            digitsMin: 6,
            digitsMax: 6,
            placeholder: "12-34-56",
        },
        payoutNote: "Only the last 4 digits of your account and sort code are stored. Full numbers are encrypted in transit.",
        dialogSubtitle: "UK bank details for this recipient's share of each payout.",
    },
    {
        code: "US",
        name: "United States",
        bankNamePlaceholder: "Chase, Bank of America, Wells Fargo…",
        accountLabel: "Account number",
        accountDigitsMin: 4,
        accountDigitsMax: 17,
        branch: {
            key: "routing_number",
            label: "Routing number",
            digitsMin: 9,
            digitsMax: 9,
            placeholder: "021000021",
        },
        payoutNote: "Only the last 4 digits of your account and routing number are stored. Full numbers are encrypted in transit.",
        dialogSubtitle: "US bank details for ACH-style payouts to this recipient.",
    },
    {
        code: "IE",
        name: "Ireland",
        bankNamePlaceholder: "AIB, Bank of Ireland, Revolut…",
        accountLabel: "Account number",
        accountDigitsMin: 6,
        accountDigitsMax: 22,
        branch: {
            key: "sort_code",
            label: "Sort code",
            digitsMin: 6,
            digitsMax: 6,
            placeholder: "93-53-12",
        },
        payoutNote: "Only masked digits are stored. Full numbers are encrypted in transit.",
        dialogSubtitle: "Irish bank details for this recipient's share of each payout.",
    },
    {
        code: "AU",
        name: "Australia",
        bankNamePlaceholder: "CommBank, NAB, ANZ, Westpac…",
        accountLabel: "Account number",
        accountDigitsMin: 5,
        accountDigitsMax: 10,
        branch: {
            key: "bsb",
            label: "BSB",
            digitsMin: 6,
            digitsMax: 6,
            placeholder: "062-000",
        },
        payoutNote: "Only masked digits are stored. Full numbers are encrypted in transit.",
        dialogSubtitle: "Australian bank details for this recipient's share of each payout.",
    },
    {
        code: "CA",
        name: "Canada",
        bankNamePlaceholder: "RBC, TD, Scotiabank, BMO…",
        accountLabel: "Account number",
        accountDigitsMin: 7,
        accountDigitsMax: 12,
        branch: {
            key: "transit_number",
            label: "Transit number",
            digitsMin: 5,
            digitsMax: 5,
            placeholder: "12345",
        },
        payoutNote: "Only masked digits are stored. Full numbers are encrypted in transit.",
        dialogSubtitle: "Canadian bank details for this recipient's share of each payout.",
    },
    {
        code: "EU",
        name: "Europe (IBAN)",
        bankNamePlaceholder: "Deutsche Bank, BNP Paribas, ING…",
        accountLabel: "IBAN",
        accountDigitsMin: 15,
        accountDigitsMax: 34,
        branch: null,
        payoutNote: "Only the last 4 characters of your IBAN are stored. Full IBAN is encrypted in transit.",
        dialogSubtitle: "IBAN for this recipient's share of each payout.",
    },
];

export function getBankingProfile(countryCode) {
    const code = (countryCode || DEFAULT_BANK_COUNTRY).toUpperCase();
    return BANKING_COUNTRIES.find((c) => c.code === code) || BANKING_COUNTRIES[0];
}

export function digitsOnly(value) {
    return String(value || "").replace(/\D/g, "");
}

export function formatSortCode(value) {
    const d = digitsOnly(value).slice(0, 6);
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}-${d.slice(2)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
}

export function formatBranchDisplay(profile, raw) {
    if (!profile?.branch) return raw || "";
    const d = digitsOnly(raw);
    if (profile.code === "GB" || profile.code === "IE") return formatSortCode(d);
    if (profile.code === "AU" && d.length >= 3) {
        return d.length <= 3 ? d : `${d.slice(0, 3)}-${d.slice(3, 6)}`;
    }
    return d;
}

export function formatBankMask(member) {
    const bank = member?.bank;
    if (!bank?.account_last4) return null;
    const name = bank.bank_name || "Bank";
    const branchLabel = bank.branch_label || (bank.routing_last4 ? "Routing" : null);
    const branchBit = branchLabel && (bank.branch_last4 || bank.routing_last4)
        ? ` · ${branchLabel} ····${bank.branch_last4 || bank.routing_last4}`
        : "";
    return `${name} ····${bank.account_last4}${branchBit}`;
}
